import React, { useContext } from "react";
import { StaticImage } from "gatsby-plugin-image";

import { Swiper, SwiperSlide } from "swiper/react";
import {
    FreeMode,
    Navigation,
    Pagination,
    Scrollbar,
    Mousewheel,
    EffectFade
} from "swiper";
import { renderNodeRule } from "datocms-structured-text-to-plain-text";
import { isParagraph, isRoot } from "datocms-structured-text-utils";

import { StructuredTextRenderer } from "../../components/StructuredTextRenderer";
import { Headline } from "../../components/Headline";
import { SectionContainer } from "../../components/SectionContainer";
import { Tagline } from "../../components/Tagline/Tagline";
import { Button } from "../../components/Button";
import { generateClassString as gcs } from "../../lib/helper/generateClassString";

import * as styles from "./testimonialBlock.module.less";
import GeneralPageContext from "../../templates/GeneralPageContext";
import t from "../../lib/translate";

export function TestimonialBlock({
    data: {
        id,
        anker,
        tagline,
        headline,
        semanticOfHeadline,
        testimonials,
        background
    },
    type,
    hasLightBackground
}) {
    const idPaginationElement = "pagination-element-".concat(id);
    const idButtonPrev = "button-prev-".concat(id);
    const idButtonNext = "button-next-".concat(id);

    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    const Testimonial = ({ testimonial }) => (
        <>
            <StructuredTextRenderer
                key={testimonial.id}
                data={testimonial.quote}
                customRules={[
                    renderNodeRule(
                        isParagraph,
                        ({
                            adapter: { renderNode },
                            children,
                            key,
                            ancestors
                        }) => {
                            if (isRoot(ancestors[0])) {
                                // this is the top-level one, give it the QUOTE-tag
                                return renderNode("p", { key }, children);
                            } else {
                                // Proceed with default paragraph rendering...
                                return renderNode("p", { key }, children);
                            }
                        }
                    )
                ]}
                contentElementType={type}
            />
            <p className={styles.author}>
                {testimonial.nameContactPerson &&
                    `${testimonial.nameContactPerson}, `}
                {testimonial.jobPosition && `${testimonial.jobPosition}, `}
                {testimonial.nameCompany && `${testimonial.nameCompany}`}
            </p>
        </>
    );

    return (
        <SectionContainer
            sectionId={anker}
            sectionClass={styles.testimonialBlockContentSection}
            containerClass={styles.testimonialBlockContainer}
            background={background}
            partialVisibility={false}
        >
            <div
                className={gcs(styles.contentContainer, [
                    showAnchorNavigation,
                    styles.anchored
                ])}
            >
                <div className={styles.imageContainer}>
                    <Tagline hasLightBackground={hasLightBackground}>
                        {tagline}
                    </Tagline>
                    <Headline
                        semanticOfHeadline={semanticOfHeadline}
                        hasLightBackground={hasLightBackground}
                    >
                        {headline}
                    </Headline>

                    <StaticImage
                        className={styles.imageBlock}
                        src="../../assets/Testimonial-Block.svg"
                        alt="dots"
                    />
                </div>
                <div>
                    {testimonials.length > 1 ? (
                        <>
                            <Swiper
                                modules={[
                                    Navigation,
                                    Pagination,
                                    FreeMode,
                                    Scrollbar,
                                    Mousewheel,
                                    EffectFade
                                ]}
                                slidesPerView={1}
                                freeMode={true}
                                effect="fade"
                                fadeEffect={{
                                    crossFade: true
                                }}
                                navigation={{
                                    prevEl: `#${idButtonPrev}`,
                                    nextEl: `#${idButtonNext}`
                                }}
                                loop={true}
                                pagination={{
                                    type: "fraction",
                                    el: `#${idPaginationElement}`,
                                    renderFraction: function (
                                        currentClass,
                                        totalClass
                                    ) {
                                        return (
                                            '<span class="' +
                                            currentClass +
                                            '"></span>' +
                                            " | " +
                                            '<span class="' +
                                            totalClass +
                                            '"></span>'
                                        );
                                    }
                                }}
                            >
                                {testimonials.map((testimonial) => (
                                    <SwiperSlide key={testimonial.id}>
                                        <Testimonial
                                            testimonial={testimonial}
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>

                            <div className={styles.sliderControls}>
                                <div
                                    id={idPaginationElement}
                                    className={styles.paginationElement}
                                />
                                <Button
                                    id={idButtonPrev}
                                    type="prev"
                                    icon="arrowLeft"
                                    aria-label={t("navigation.button.prev")}
                                />
                                <Button
                                    id={idButtonNext}
                                    type="next"
                                    icon="arrowRight"
                                    aria-label={t("navigation.button.next")}
                                />
                            </div>
                        </>
                    ) : (
                        <Testimonial testimonial={testimonials[0]} />
                    )}
                </div>
            </div>
        </SectionContainer>
    );
}
